import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { Button } from '@factorialco/deprecated-design-system'
import { CreatePaymentRequestDocument, CycleRouteMetaDocument } from 'generated/resources/graphql'
import moment from 'moment'

import DayPicker from 'modules/core/components/DayPicker'
import Modal from 'modules/core/components/Modal'
import { useMutation } from 'modules/core/hooks/useMutation'
import { dateUIToBackend } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

type CreatePaymentModalProps = {
  onClose: () => void
  onSuccess?: () => void
}

const CreatePaymentModal = ({ onClose, onSuccess }: CreatePaymentModalProps) => {
  const { id } = useParams<{ id: string }>()
  const [selectedDate, setSelectedDate] = useState<string>('')
  const [dateError, setDateError] = useState<string>('')

  const [createPaymentRequest, { loading }] = useMutation(CreatePaymentRequestDocument, {
    extractorFn: (result) =>
      result.compensationsMutations.createWithItemsCompensationsPaymentRequest,
    refetchQueries: [CycleRouteMetaDocument],
  })

  const handleDateChange = (value: string) => {
    setSelectedDate(value)
    if (dateError && value) {
      setDateError('')
    }
  }

  const onSubmit = async () => {
    if (!selectedDate) {
      setDateError(i18n.t('common.validations.empty'))
      return
    }

    const result = await createPaymentRequest({
      variables: {
        payrollRunId: String(id),
        dueDate: dateUIToBackend(selectedDate),
      },
    })

    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('compensations.payments.create_modal.success_message'),
      })

      onSuccess?.()
      onClose()
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('compensations.payments.create_modal.error_message'),
      })
    }
  }

  return (
    <Modal
      illustration="money-spreadsheet"
      title={i18n.t('compensations.payments.create_modal.send_employees_for_payment')}
      description={i18n.t('compensations.payments.create_modal.description')}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <>
          <Button type="secondary" onClick={onClose} disabled={loading}>
            {i18n.t('compensations.payments.create_modal.cancel_button')}
          </Button>
          <Button onClick={onSubmit} disabled={loading}>
            {i18n.t('compensations.payments.create_modal.send_button')}
          </Button>
        </>
      }
    >
      <DayPicker
        label={i18n.t('compensations.payments.create_modal.due_date')}
        value={selectedDate}
        onChange={handleDateChange}
        placeholder={i18n.t('compensations.payments.create_modal.due_date_placeholder')}
        minDate={moment().startOf('day')}
        mandatory
        error={dateError}
      />
    </Modal>
  )
}

export default CreatePaymentModal
